import { Component, OnInit, OnDestroy } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Idle, DEFAULT_INTERRUPTSOURCES } from '@ng-idle/core';

import { CommonService } from '../../services/common.service';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { AppDataService } from 'app/modules/authentication/services/appdata.service';
import {
  AuthenticationPath,
  ControlPanelPath,
} from 'app/core/common/route-constant';
import { RouteHistoryService } from 'app/core/services/route-history.service';
import { Subscription } from 'rxjs';
import { MsalService } from '@azure/msal-angular';

@Component({
  selector: 'app-admin-layout',
  templateUrl: './admin-layout.component.html',
  styleUrls: ['./admin-layout.component.scss'],
})
export class AdminLayoutComponent implements OnInit, OnDestroy {
  items: MenuItem[];
  //#region Timeout
  sessionTimeout = 600; // 600 seconds = 10 minutes
  timedOut: boolean = false;
  session_expired: boolean = false;
  idleState: string = 'Active';
  private idleSubscriptions: Subscription[] = [];
  private lastActivityWriteTime = 0;
  private readonly ACTIVITY_THROTTLE = 5000;
  private activityHandler!: () => void;
  private storageHandler!: (event: StorageEvent) => void;
  private beforeUnloadHandler!: () => void;

  //#endregion
  constructor(
    private commonSerivce: CommonService,
    private appData: AppDataService,
    private router: Router,
    private spinner: NgxSpinnerService,
    private routeHistoryService: RouteHistoryService,
    private idle: Idle,
    private msal: MsalService
  ) {}

  ngOnInit(): void {
    this.items = [
      {
        label: this.appData.connectedUser.email,
        style: { 'margin-left': 'auto' },
        icon: 'pi pi-fw pi-user',
        command: (event: any) => {},
      },
      {
        label: 'Logout',
        icon: 'pi pi-fw pi-power-off',
        command: (event: any) => {
          this.logout();
        },
      },
    ];
    //#region idle case
    // 1️⃣ If app was closed for more than 10 minutes, log out immediately
    const lastActive = localStorage.getItem('lastActive');
    if (lastActive && Date.now() - parseInt(lastActive, 10) > this.sessionTimeout * 1000) {
      this.appData.logout();
      this.router.navigate([`${AuthenticationPath.Login}`]);
      return;
    }
    localStorage.removeItem('lastActive');

    // 2️⃣ Start Idle Monitoring
    this.idle.setIdle(600);
    this.idle.setTimeout(30);
    this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

    this.idleSubscriptions.push(
      this.idle.onIdleStart.subscribe(() => {
        this.idleState = 'You have gone idle!';
      }),
      this.idle.onTimeout.subscribe(() => {
        this.idleState = 'Session timed out!';
        this.timedOut = true;
        this.session_expired = true;
        this.appData.logout();
        this.router.navigate([`${AuthenticationPath.Login}`]);
      }),
      this.idle.onIdleEnd.subscribe(() => {
        this.idleState = 'Active';
      })
    );

    this.idle.watch();

    // 3️⃣ Listen for activity (cross-tab sync) and lifecycle events
    this.addActivityListeners();
    //#endregion

    this.listenToLoading();
    if (
      this.appData.connectedUser.show_stats == 0 &&
      !this.appData.connectedUser.control_panel_active
    ) {
      this.logout();
    }
  }

  logout(): void {
    const mode = localStorage.getItem('authMode') as 'msal' | 'local' | null;
    this.appData.navigateUrl = null;
    if (mode === 'msal' && this.msal.instance.getActiveAccount()) {
      this.msal.logoutRedirect({
        postLogoutRedirectUri: location.origin,
      });
      this.appData.logout();
      return;
    } else {
      // Clear all cookies with path handling
      document.cookie.split(';').forEach(function (c) {
        document.cookie = c
          .replace(/^ +/, '')
          .replace(/=.*/, '=;expires=' + new Date().toUTCString() + ';path=/');
      });
      this.appData.logout();
      this.router.navigate([`${AuthenticationPath.Login}`]);
    }
  }

  listenToLoading(): void {
    this.commonSerivce.data$.subscribe((loading) => {
      if (loading == 'show') {
        this.spinner.show();
      } else {
        this.spinner.hide();
      }
    });
  }

  openHomePage(): void {
    this.router.navigate([`${ControlPanelPath.ControlPanel}` + '/']);
  }

  // #region Timeout

  addActivityListeners() {
    // Throttled handler: writes to localStorage for cross-tab sync only
    // ng-idle's DEFAULT_INTERRUPTSOURCES already resets the idle timer in the current tab
    this.activityHandler = () => {
      const now = Date.now();
      if (now - this.lastActivityWriteTime > this.ACTIVITY_THROTTLE) {
        this.lastActivityWriteTime = now;
        localStorage.setItem('lastActivity', now.toString());
      }
    };

    // Single consolidated storage handler for both cross-tab cases
    this.storageHandler = (event: StorageEvent) => {
      if (event.key === 'lastActivity') {
        this.idle.watch(); // Reset idle timer when another tab is active
      } else if (event.key === 'logout') {
        this.router.navigate([`${AuthenticationPath.Login}`]);
      }
    };

    // Save timestamp on tab close so reopen can check elapsed time
    this.beforeUnloadHandler = () => {
      localStorage.setItem('lastActive', Date.now().toString());
    };

    ['mousemove', 'keydown', 'click'].forEach(event => {
      window.addEventListener(event, this.activityHandler);
    });
    window.addEventListener('storage', this.storageHandler);
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  ngOnDestroy() {
    this.idleSubscriptions.forEach(sub => sub.unsubscribe());
    this.idle.stop();
    ['mousemove', 'keydown', 'click'].forEach(event => {
      window.removeEventListener(event, this.activityHandler);
    });
    window.removeEventListener('storage', this.storageHandler);
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
  }

  onLogout() {
    this.appData.navigateUrl = null;
    this.appData.logout();
    this.router.navigate([`${AuthenticationPath.Login}`]);
  }

  // #endregion
}
